import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Breadcrumbs as MuiBreadcrumbs, Typography } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

// Path segment to label mapping (for dynamic segments)
const segmentLabels = {
  new: 'Add',
  details: 'Details',
  candidates: 'Candidates',
  purchaseHistory: 'Purchase History',
  smsHistory: 'SMS History',
  'income-categories': 'Income Categories',
  'expenditure-categories': 'Expenditure Categories',
  'asset-categories': 'Asset Categories',
  'course-categories': 'Course Categories',
  'course-types': 'Course Types',
  'general-report': 'General Report',
  'financial-report': 'Financial Report',
  'membership-report': 'Membership Report',
  // 'branch-data': 'Branch Data',
  'events-data': 'Events Data',
  attendees: 'Attendees',
  'contact-us': 'Contact Us',
};

const pathToLabel = { '/dashboard': 'Dashboard', '/dashboard/profile': 'Settings' };

// Build from menu structure
const menuItems = [
  { text: 'Dashboard', path: '/dashboard', exact: true },
  { text: 'Audit Log', path: '/dashboard/audit' },
  {
    text: 'Members',
    children: [
      { text: 'Members List', path: '/dashboard/members' },
      { text: 'Verify Member', path: '/dashboard/verify-member' },
    ],
  },
  {
    text: 'Finance',
    children: [
      { text: 'Income', path: '/dashboard/income' },
      { text: 'Income Categories', path: '/dashboard/income-categories' },
      { text: 'Expenditure', path: '/dashboard/expenditure' },
      { text: 'Expenditure Categories', path: '/dashboard/expenditure-categories' },
      { text: 'Invoices', path: '/dashboard/invoices' },
    ],
  },
  {
    text: 'HR',
    children: [
      { text: 'Positions', path: '/dashboard/positions' },
      { text: 'Executives', path: '/dashboard/executives' },
      { text: 'Elections', path: '/dashboard/hr/elections' },
      { text: 'Events', path: '/dashboard/events' },
      { text: 'Staff', path: '/dashboard/staff' },
      { text: 'Group', path: '/dashboard/group' },
    ],
  },
  {
    text: 'Asset',
    children: [
      { text: 'Asset Categories', path: '/dashboard/asset-categories' },
      { text: 'Asset', path: '/dashboard/asset' },
    ],
  },
  {
    text: 'Report',
    children: [
      { text: 'General Report', path: '/dashboard/general-report' },
      { text: 'Financial Report', path: '/dashboard/financial-report' },
      { text: 'Membership Report', path: '/dashboard/membership-report' },
      // { text: 'Branch Data', path: '/dashboard/reports/branch-data' },
      { text: 'Events Data', path: '/dashboard/reports/events-data' },
    ],
  },
  // {
  //   text: 'Branches',
  //   children: [{ text: 'Levels', path: '/dashboard/levels' }],
  // },
  {
    text: 'Education',
    children: [
      { text: 'Course Categories', path: '/dashboard/course-categories' },
      { text: 'Course Types', path: '/dashboard/course-types' },
      { text: 'Courses', path: '/dashboard/courses' },
      { text: 'Exams', path: '/dashboard/exams' },
      { text: 'Exam Candidates', path: '/dashboard/exam-candidates' },
      { text: 'Exam Marks', path: '/dashboard/exam-marks' },
    ],
  },
  { text: 'Communication', path: '/dashboard/communication' },
];

menuItems.forEach((item) => {
  if (item.path) {
    pathToLabel[item.path] = item.text;
  }
  if (item.children) {
    item.children.forEach((child) => {
      pathToLabel[child.path] = child.text;
    });
  }
});

// Parent path labels (for intermediate segments)
pathToLabel['/dashboard/members'] = 'Members';
pathToLabel['/dashboard/hr'] = 'HR';
pathToLabel['/dashboard/reports'] = 'Reports';
// Communication sub-routes
pathToLabel['/dashboard/communication/purchaseHistory'] = 'Purchase History';
pathToLabel['/dashboard/communication/smsHistory'] = 'SMS History';

function getBreadcrumbs(pathname) {
  if (pathname === '/dashboard' || pathname === '/dashboard/') {
    return [{ label: 'Dashboard', path: null }];
  }

  const segments = pathname.replace(/^\/dashboard\/?/, '').split('/').filter(Boolean);
  const crumbs = [{ label: 'Dashboard', path: '/dashboard' }];

  let currentPath = '/dashboard';
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    currentPath = currentPath + (currentPath.endsWith('/') ? '' : '/') + seg;

    let label = pathToLabel[currentPath] || segmentLabels[seg];
    if (!label) {
      if (seg === 'new') {
        label = 'Add';
      } else if (seg === 'details') {
        label = 'Details';
      } else if (/^\d+$/.test(seg) || /^[a-f0-9-]{36}$/i.test(seg)) {
        label = 'Edit';
      } else if (i === segments.length - 1 && segments[i - 1] === 'elections') {
        label = 'Candidates';
      } else {
        label = seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, ' ');
      }
    }

    const isLast = i === segments.length - 1;
    crumbs.push({
      label,
      path: isLast ? null : currentPath,
    });
  }

  return crumbs;
}

export default function Breadcrumbs() {
  const location = useLocation();
  const crumbs = getBreadcrumbs(location.pathname);

  if (crumbs.length <= 1) {
    return null;
  }

  return (
    <MuiBreadcrumbs
      separator={<NavigateNextIcon fontSize="small" sx={{ color: '#66736C' }} />}
      sx={{ mb: 2, '& .MuiBreadcrumbs-ol': { alignItems: 'center' } }}
    >
      {crumbs.map((crumb, i) =>
        crumb.path ? (
          <Link
            key={crumb.path}
            to={crumb.path}
            style={{
              color: '#66736C',
              textDecoration: 'none',
              fontSize: 12,
            }}
          >
            {crumb.label}
          </Link>
        ) : (
          <Typography key={i} sx={{ fontSize: 12, fontWeight: 500, color: '#14261F' }}>
            {crumb.label}
          </Typography>
        )
      )}
    </MuiBreadcrumbs>
  );
}
